import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Layers, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WordCard } from "@/components/WordCard";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://images.unsplash.com/photo-1718445464820-083b6b7cc99b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600&fit=crop";

const FEATURED_IDS = ["w-skog", "w-äpple", "w-glädje"];

const STEPS = [
  { icon: BookOpen, title: "Explore", desc: "Browse curated word collections paired with beautiful imagery." },
  { icon: Layers, title: "Learn", desc: "Use flashcards to memorize words naturally and at your own pace." },
  { icon: Brain, title: "Remember", desc: "Test your knowledge with gentle quizzes and track your growth." },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [wRes, cRes] = await Promise.all([
          axios.get(`${API}/words`),
          axios.get(`${API}/categories`),
        ]);
        const featWords = wRes.data.filter((w) => FEATURED_IDS.includes(w.id));
        if (featWords.length === 0) setFeatured(wRes.data.slice(0, 3));
        else setFeatured(featWords);
        setCategories(cRes.data);
      } catch (e) {
        console.error("Failed to load data", e);
      }
    };
    load();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[85vh] min-h-[600px] flex items-end" data-testid="hero-section">
        <img
          src={HERO_IMG}
          alt="Swedish landscape with red cottage by a calm lake"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-white/80 text-sm uppercase tracking-[0.25em] font-body font-medium mb-4">
              Swedish Visual Learning
            </p>
            <h1 className="font-heading text-5xl md:text-7xl font-medium text-white leading-tight tracking-tight max-w-2xl">
              Lär dig svenska<br />med känsla
            </h1>
            <p className="mt-6 text-white/85 text-lg md:text-xl font-body max-w-lg leading-relaxed">
              Discover Swedish through beauty and connection. Each word finds its picture.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/gallery">
                <Button
                  className="bg-nordic-blue text-white hover:bg-nordic-blue/90 rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl"
                  style={{ transition: "background-color 0.3s ease, box-shadow 0.3s ease" }}
                  data-testid="hero-explore-btn"
                >
                  Start Exploring <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/flashcards">
                <Button
                  variant="outline"
                  className="bg-nordic-paper/15 backdrop-blur-md text-white border-white/30 hover:bg-nordic-paper/25 rounded-full px-8 py-6 text-lg font-medium"
                  style={{ transition: "background-color 0.3s ease" }}
                  data-testid="hero-flashcards-btn"
                >
                  Try Flashcards
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Words */}
      <section className="py-24 md:py-32" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-[0.2em] text-nordic-text-muted font-semibold mb-3">Word of the day</p>
            <h2 className="font-heading text-4xl md:text-5xl font-normal text-nordic-text tracking-tight mb-4">
              Discover new words
            </h2>
            <p className="text-nordic-text-secondary text-base md:text-lg max-w-xl mb-12">
              Every Swedish word tells a story. Explore words paired with images that capture their essence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((word, i) => (
              <WordCard key={word.id} word={word} index={i} showDetails />
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 md:py-32 bg-nordic-muted" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-[0.2em] text-nordic-text-muted font-semibold mb-3">Collections</p>
            <h2 className="font-heading text-4xl md:text-5xl font-normal text-nordic-text tracking-tight mb-12">
              Explore by category
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((cat, i) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <Link
                  to={`/gallery/${cat.slug}`}
                  className="group block relative overflow-hidden rounded-2xl h-64"
                  data-testid={`category-card-${cat.slug}`}
                >
                  <img
                    src={cat.image_url}
                    alt={cat.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105"
                    style={{ transition: "transform 0.6s ease" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2C3E50]/70 via-[#2C3E50]/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <span className="text-white/70 text-xs uppercase tracking-wider font-semibold">{cat.swedish_name}</span>
                    <h3 className="font-heading text-2xl text-white font-medium mt-1">{cat.name}</h3>
                    <p className="text-white/80 text-sm mt-1">{cat.word_count} words</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-nordic-text-muted font-semibold mb-3">How it works</p>
            <h2 className="font-heading text-4xl md:text-5xl font-normal text-nordic-text tracking-tight">
              Learning made beautiful
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.15 }}
                className="bg-nordic-paper/50 backdrop-blur-sm p-8 rounded-2xl border border-white/60 shadow-sm"
                data-testid={`step-${step.title.toLowerCase()}`}
              >
                <div className="w-12 h-12 rounded-xl bg-nordic-blue/10 flex items-center justify-center mb-5">
                  <step.icon className="w-6 h-6 text-nordic-blue" />
                </div>
                <h3 className="font-heading text-xl font-medium text-nordic-text mb-2">{step.title}</h3>
                <p className="text-nordic-text-secondary text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-nordic-blue" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-normal text-white tracking-tight mb-6">
              Ready to learn Swedish?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-lg mx-auto">
              Start with just one word. Let the beauty of Swedish unfold before you.
            </p>
            <Link to="/gallery">
              <Button
                className="bg-nordic-paper text-nordic-blue hover:bg-nordic-paper/90 rounded-full px-10 py-6 text-lg font-medium shadow-lg"
                style={{ transition: "background-color 0.3s ease" }}
                data-testid="cta-explore-btn"
              >
                Explore All Words <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
